import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Users, Bus, TrendingDown, AlertTriangle } from 'lucide-react';

// --------- HELPERS ----------
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function computeSafeTooltipPosition({ barRect, containerRect, tipW, tipH, prefAbove = true }) {
  const PADDING = 8;
  const OFFSET  = 10;

  // centro da barra relativo ao container
  let x = (barRect.left - containerRect.left) + (barRect.width / 2);

  const yTop    = (barRect.top - containerRect.top) - OFFSET - tipH; // acima
  const yBottom = (barRect.bottom - containerRect.top) + OFFSET;      // abaixo

  let placeAbove = prefAbove;
  let y = placeAbove ? yTop : yBottom;

  // se acima não cabe, tenta abaixo
  if (placeAbove && y < PADDING) {
    placeAbove = false;
    y = yBottom;
  }

  const containerH = containerRect.height;
  // se abaixo não cabe, volta acima
  if (!placeAbove && (y + tipH + PADDING > containerH)) {
    placeAbove = true;
    y = yTop;
  }

  // clamp final
  y = clamp(y, PADDING, Math.max(PADDING, containerH - tipH - PADDING));

  // como usamos translateX(-50%), clamp considerando meia-largura
  const minCenter = PADDING + tipW / 2;
  const maxCenter = containerRect.width - PADDING - tipW / 2;
  x = clamp(x, minCenter, maxCenter);

  return { x, y, placeAbove };
}
// ----------------------------

export default function PassengerChart() {
  const [animatedData, setAnimatedData] = useState([]);
  const [hoveredBarKey, setHoveredBarKey] = useState(null);   // `${index}-offered` | `${index}-transported`
  const [tooltipData, setTooltipData] = useState(null);        // { year, value, label }
  const [tooltipPos,  setTooltipPos]  = useState(null);        // { x, y, placeAbove }
  const [isMobile,    setIsMobile]    = useState(false);

  // Refs de layout para tooltip consistente
  const scrollRef       = useRef(null);        // wrapper com overflow-x no mobile
  const innerRefDesktop = useRef(null);        // container RELATIVE (desktop)
  const innerRefMobile  = useRef(null);        // container RELATIVE (mobile)
  const tooltipRef      = useRef(null);        // mede tamanho do tooltip

  const passengerData = [
    { year: 2010, offered: 96.3, transported: 46.7 }, { year: 2011, offered: 92.5, transported: 45.4 },
    { year: 2012, offered: 91.5, transported: 47.6 }, { year: 2013, offered: 93.4, transported: 46.2 },
    { year: 2014, offered: 78.5, transported: 42.8 }, { year: 2015, offered: 74.0, transported: 40.1 },
    { year: 2016, offered: 71.3, transported: 40.8 }, { year: 2017, offered: 63.8, transported: 39.6 },
    { year: 2018, offered: 62.5, transported: 41.1 }, { year: 2019, offered: 60.8, transported: 40.6 },
    { year: 2020, offered: 38.8, transported: 24.6 }, { year: 2021, offered: 50.4, transported: 31.2 },
    { year: 2022, offered: 60.0, transported: 41.9 }, { year: 2023, offered: 68.7, transported: 43.0 },
    { year: 2024, offered: 63.4, transported: 40.5 }
  ];

  const CHART_HEIGHT = 320; // px
  const maxValue = Math.max(...passengerData.map(d => Math.max(d.offered, d.transported)));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setAnimatedData(passengerData), 400);
    return () => clearTimeout(timer);
  }, []);

  const getBarHeight = (value) => (value / maxValue) * (CHART_HEIGHT - 40);

  const clsOffered = (active) =>
    `rounded-t cursor-pointer transition-all duration-300 ${active ? 'bg-emerald-600' : 'bg-emerald-500 hover:bg-emerald-600'}`;
  const clsTransported = (active) =>
    `rounded-t cursor-pointer transition-all duration-300 ${active ? 'bg-sky-600' : 'bg-sky-500 hover:bg-sky-600'}`;

  // Mostrar tooltip com posicionamento seguro (duplo RAF para iOS/Safari)
  const showTooltip = (item, key, targetEl) => {
    const container = (isMobile ? innerRefMobile : innerRefDesktop).current;
    if (!container) return;

    const barRect       = targetEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    setHoveredBarKey(key);

    const isOffered = key.endsWith('offered');
    const value     = isOffered ? item.offered : item.transported;
    const label     = isOffered ? 'Assentos Ofertados' : 'Passageiros Transportados';

    // 1) Renderiza o tooltip
    setTooltipData({ year: item.year, value, label });

    // 2) Mede no(s) próximo(s) frame(s) para garantir que o DOM foi atualizado
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const tip = tooltipRef.current;
        const tipW = tip ? tip.offsetWidth  : 140;
        const tipH = tip ? tip.offsetHeight : 44;

        const { x, y, placeAbove } = computeSafeTooltipPosition({
          barRect,
          containerRect,
          tipW,
          tipH,
          // no mobile preferimos abaixo; no desktop, acima
          prefAbove: !isMobile
        });

        setTooltipPos({ x, y, placeAbove });
      });
    });
  };

  const handleEnter = (item, key) => (e) => showTooltip(item, key, e.currentTarget);
  const handleLeave = () => {
    setHoveredBarKey(null);
    setTooltipData(null);
    setTooltipPos(null);
  };

  // Handlers com Pointer Events (mouse + touch)
  const getBarHandlers = (item, key) => ({
    onPointerDown:  (e) => showTooltip(item, key, e.currentTarget),              // tap/click abre tooltip
    onPointerEnter: (e) => !isMobile && showTooltip(item, key, e.currentTarget), // hover desktop
    onPointerMove:  (e) => !isMobile && showTooltip(item, key, e.currentTarget),
    onPointerLeave: handleLeave,
  });

  // --- mobile layout ---
  const BAR_W_MOBILE = 16;
  const GAP_BETWEEN_BARS = 6;    // entre offered e transported
  const GAP_BETWEEN_GROUPS = 12; // entre anos
  const GROUP_W_MOBILE = (BAR_W_MOBILE * 2) + GAP_BETWEEN_BARS + GAP_BETWEEN_GROUPS;
  const contentWidthMobile = (passengerData.length * GROUP_W_MOBILE) + 16; // padding
  const touchSafeStyle = { touchAction: 'manipulation', userSelect: 'none' };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">Evolução do Transporte de Passageiros</CardTitle>
          <p className="text-center text-gray-600">Oferta de assentos e passageiros transportados no modal rodoviário (em milhões)</p>
        </CardHeader>
        <CardContent>
          {isMobile ? (
            // --- MOBILE: scroll horizontal, grupos por ano ---
            <div className="overflow-x-auto no-scrollbar" ref={scrollRef} onScroll={handleLeave}>
              <div
                className="relative flex items-end px-4"
                ref={innerRefMobile}
                style={{ height: CHART_HEIGHT, width: contentWidthMobile, paddingTop: 6 }}
              >
                {passengerData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end" style={{ width: GROUP_W_MOBILE }}>
                    <div className="flex items-end" style={{ gap: GAP_BETWEEN_BARS }}>
                      {/* Ofertados */}
                      <div
                        className={clsOffered(hoveredBarKey === `${index}-offered`)}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.offered) : 0}px`,
                          transitionDelay: `${index * 40}ms`,
                          transform: hoveredBarKey === `${index}-offered` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-offered`)}
                      />
                      {/* Transportados */}
                      <div
                        className={clsTransported(hoveredBarKey === `${index}-transported`)}
                        style={{
                          ...touchSafeStyle,
                          width: BAR_W_MOBILE,
                          height: `${animatedData[index] ? getBarHeight(item.transported) : 0}px`,
                          transitionDelay: `${index * 40 + 16}ms`,
                          transform: hoveredBarKey === `${index}-transported` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-transported`)}
                      />
                    </div>
                    <span className="text-[10px] mt-1 text-gray-500">{String(item.year).slice(2)}</span>
                  </div>
                ))}

                {tooltipData && (
                  <div
                    ref={tooltipRef}
                    className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                    style={{
                      left: tooltipPos ? tooltipPos.x : -9999,
                      top: tooltipPos ? tooltipPos.y : -9999,
                      transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                    }}
                  >
                    <div className="text-sm font-semibold">{tooltipData.year}</div>
                    <div className="text-xs">{tooltipData.label}: {tooltipData.value.toFixed(1)} M</div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // --- DESKTOP: duas barras por ano ---
            <div className="h-80 px-4 relative" ref={innerRefDesktop}>
              <div className="flex items-end justify-between h-full gap-2">
                {passengerData.map((item, index) => (
                  <div key={item.year} className="flex flex-col items-center justify-end h-full">
                    <div className="flex items-end gap-1 h-full">
                      {/* Ofertados */}
                      <div
                        className={clsOffered(hoveredBarKey === `${index}-offered`) + " w-4"}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.offered) : 0}px`,
                          transitionDelay: `${index * 50}ms`,
                          transform: hoveredBarKey === `${index}-offered` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-offered`)}
                      />
                      {/* Transportados */}
                      <div
                        className={clsTransported(hoveredBarKey === `${index}-transported`) + " w-4"}
                        style={{
                          ...touchSafeStyle,
                          height: `${animatedData[index] ? getBarHeight(item.transported) : 0}px`,
                          transitionDelay: `${index * 50 + 20}ms`,
                          transform: hoveredBarKey === `${index}-transported` ? 'translateY(-2px)' : 'translateY(0)'
                        }}
                        {...getBarHandlers(item, `${index}-transported`)}
                      />
                    </div>
                    <span className="text-xs mt-2 text-gray-500">{item.year}</span>
                  </div>
                ))}
              </div>

              {tooltipData && (
                <div
                  ref={tooltipRef}
                  className="absolute z-10 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-lg whitespace-nowrap pointer-events-none"
                  style={{
                    left: tooltipPos ? tooltipPos.x : -9999,
                    top: tooltipPos ? tooltipPos.y : -9999,
                    transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)'
                  }}
                >
                  <div className="text-sm font-semibold">{tooltipData.year}</div>
                  <div className="text-xs">{tooltipData.label}: {tooltipData.value.toFixed(1)} M</div>
                </div>
              )}
            </div>
          )}

          {/* Legenda */}
          <div className="mt-6 flex justify-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-500 rounded" />
              <Users className="w-4 h-4 mr-1" />
              <span>Assentos Ofertados</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-sky-500 rounded" />
              <Bus className="w-4 h-4 mr-1" />
              <span>Passageiros Transportados</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Análise dos Principais Fatores */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-gradient-to-br from-red-50 to-orange-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-red-900 flex items-center gap-2">
              <TrendingDown className="w-6 h-6" />
              Declínio Estrutural do Setor
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Impacto da Pandemia (2020)</h4>
              <p className="text-sm text-gray-700">
                A oferta despencou para 38,8 milhões de assentos e apenas 24,6 milhões de passageiros transportados,
                o menor patamar da série histórica devido às restrições sanitárias.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-red-800 mb-2">Queda Continuada (2010-2024)</h4>
              <p className="text-sm text-gray-700">
                Oferta recuou 3,2% no período, de 96,3 milhões para 63,4 milhões de assentos.
                Passageiros transportados caíram 1,1%, de 46,7 para 40,5 milhões.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-purple-100">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Fatores da Transformação Setorial
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Popularização do Setor Aéreo</h4>
              <p className="text-sm text-gray-700">
                Crescimento da aviação comercial com preços mais competitivos reduziu a demanda
                por viagens rodoviárias de longa distância.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Ascensão de Plataformas Digitais</h4>
              <p className="text-sm text-gray-700">
                Aplicativos de fretamento eventual oferecem viagens mais baratas e flexíveis,
                competindo diretamente com o modelo tradicional de concessões.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recuperação e Fretamento */}
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-green-900">Setor de Fretamento: Contra-Tendência</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">Crescimento do Fretamento</h4>
              <p className="text-sm text-gray-700">
                Em 2024, mais de 13 milhões de passageiros utilizaram fretamento (+3,7% vs 2023),
                o maior número da série histórica.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">Novo Marco Regulatório</h4>
              <p className="text-sm text-gray-700">
                Resolução ANTT nº 6.033/2023 trouxe maior segurança jurídica e prevê
                R$ 3,5 bi em investimentos para renovação da frota.
              </p>
            </div>
            <div className="bg-white/60 p-4 rounded-lg">
              <h4 className="font-semibold text-green-800 mb-2">Destinos Preferenciais</h4>
              <p className="text-sm text-gray-700">
                São Paulo, Minas Gerais, Rio de Janeiro, Santa Catarina e Paraná concentraram
                as maiores demandas por fretamento em 2024.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Perspectivas e Desafios */}
      <Card className="bg-gradient-to-r from-indigo-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-indigo-900">Perspectivas e Adaptações do Setor</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-700 leading-relaxed">
            O setor rodoviário de passageiros enfrenta uma transformação estrutural profunda. Enquanto o modelo tradicional
            de linhas regulares perde espaço para o transporte aéreo e plataformas digitais, emerge um novo paradigma
            baseado em flexibilidade, tecnologia e preços competitivos. O crescimento do fretamento e as novas regulamentações
            sinalizam que o futuro do transporte rodoviário de passageiros passará pela adaptação aos novos hábitos de
            consumo e pela integração com tecnologias digitais, mantendo sua relevância na matriz de transportes brasileira.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
